<template>
  <v-card>
    <form-header
      :fullscreen="true"
      :form="form"
      :title='$lang("company-invoices.title_create")'
    >
      <template v-slot:left-buttons>
        <v-btn icon dark @click="cancel()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
      <template v-slot:toolbar-items>
        <v-btn dark text @click="submit()" :loading="loading">{{ $lang("main.crud.save") }}</v-btn>
      </template>
    </form-header>
    <v-card-text>
      <v-container v-show="!initialized">
        <v-row>
          <v-col cols="12" xs="12" md="4" v-for="i in $range(3)">
            <v-skeleton-loader :tile="false" :boilerplate="false" type="text" class="mt-10"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" xs="12" md="12">
            <v-skeleton-loader :tile="false" :boilerplate="false" type="text" class="mt-10"></v-skeleton-loader>
          </v-col>
        </v-row>
      </v-container>
      <v-container v-show="initialized" fluid>
        <v-row>
          <v-col cols="12" xs="12" md="6">
            <v-company
              @changed="companyChanged"
              :item="company"
              :label="$lang('companies.title')"
            />
            <v-row>
              <v-col>
                <v-text-field
                  v-model="form.sdi_id"
                  label="SDI Id"
                  dense
                />
              </v-col>
              <v-col>
                <v-text-field
                  v-model="form.filename"
                  label="Filename"
                  dense
                />
              </v-col>
              <v-col>
                <v-text-field
                  v-model="form.submission_id"
                  label="Filename"
                  dense
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" xs="12" md="6">
            <v-select
              v-model="form.type"
              :items="$in('ConstDocumentTypes')"
              :label="$lang('company-invoices.fields.type')"
            />
            <v-date
              v-model="form.date"
              :label="$lang('company-invoices.fields.date')"
              :error="!!validation.date"
              :error-messages="!!validation.date ? validation.date : []"
              dense
            />
            <v-text-field
              v-model="form.invoice_n"
              :label="$lang('company-invoices.fields.invoice_n')"
              :error="!!validation.invoice_n"
              :error-messages="!!validation.invoice_n ? validation.invoice_n : []"
              dense
            />
            <v-row>
              <v-col cols="3">
                <v-text-field
                  :label="$lang('main.words.year')"
                  v-model="seriesPrefix"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="9">
                <v-select
                  v-model="invoiceSeries"
                  :items="seriesList"
                  :label="$lang('company-invoices.fields.invoice_series')"
                  :error="!!validation.invoice_series"
                  :error-messages="!!validation.invoice_series ? validation.invoice_series : []"
                  @change="seriesChanged"
                  dense
                />
              </v-col>
            </v-row> 
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" xs="12" md="12">
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <td colspan="5" class="text-center">
                      <h2>{{ $lang('company-invoices.labels.details') }}</h2>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="5" class="text-right">
                      <v-btn @click="addDetail()" color="success" small>{{ $lang('main.crud.add') }}</v-btn>
                    </td>
                  </tr>
                  <tr>
                    <th class="text-left" style="width: 50%">{{ $lang('company-invoices.fields.description') }}</th>
                    <th class="text-left" style="max-width: 13%">{{ $lang('company-invoices.fields.vat_type') }}</th>
                    <th class="text-left" style="width: 15%">{{ $lang('company-invoices.fields.vat_amount') }}</th>
                    <th class="text-left" style="width: 12%">{{ $lang('company-invoices.fields.amount') }}</th>
                    <th class="text-center" style="width: 10%">{{ $lang('main.crud.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(detail, index) in form.details" :key="index">
                    <td>
                      <v-text-field
                        v-model="detail.company_payment_id"
                        dense
                        :error="!!validation[`details.${index}.company_payment_id`]"
                        :error-messages="!!validation[`details.${index}.company_payment_id`] ? validation[`details.${index}.company_payment_id`] : []"
                      />
                      <v-textarea
                        v-model="detail.description"
                        dense
                        :error="!!validation[`details.${index}.description`]"
                        :error-messages="!!validation[`details.${index}.description`] ? validation[`details.${index}.description`] : []"
                      />
                    </td>
                    <td style="max-width: 13%">
                      <v-text-field
                        v-model="detail.vat_type"
                        dense
                        :error="!!validation[`details.${index}.vat_type`]"
                        :error-messages="!!validation[`details.${index}.vat_type`] ? validation[`details.${index}.vat_type`] : []"
                        suffix="%"
                      />
                      <v-select
                        v-if="detail.vat_type == 0"
                        v-model="detail.vat_exclusion"
                        :items="$in('ConstInvoiceVatExclusionIT')"
                        dense
                        :error="!!validation[`details.${index}.vat_exclusion`]"
                        :error-messages="!!validation[`details.${index}.vat_exclusion`] ? validation[`details.${index}.vat_exclusion`] : []"
                      />
                    </td>
                    <td>
                      <v-text-field
                        v-model="detail.vat_amount"
                        :disabled="true"
                        dense
                        :error="!!validation[`details.${index}.vat_amount`]"
                        :error-messages="!!validation[`details.${index}.vat_amount`] ? validation[`details.${index}.vat_amount`] : []"
                      />
                    </td>
                    <td>
                      <v-text-field
                        v-model="detail.amount"
                        dense
                        :error="!!validation[`details.${index}.amount`]"
                        :error-messages="!!validation[`details.${index}.amount`] ? validation[`details.${index}.amount`] : []"
                      />
                    </td>
                    <td>
                      <div class="text-right">
                        <v-icon @click="addDetail()" color="success">mdi-plus</v-icon>
                        <v-icon @click="removeDetail(index)" color="error">mdi-minus</v-icon>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" xs="12" md="12">
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td colspan="3" class="text-center">
                      <h2>{{ $lang('company-invoices.labels.totals') }}</h2>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <v-text-field
                        v-model="totals.net_amount"
                        readonly
                        :label="$lang('company-invoices.totals.net_amount')"
                      />
                    </td>
                    <td>
                      <v-text-field
                        v-model="totals.vat_amount"
                        readonly
                        :label="$lang('company-invoices.totals.vat_amount')"
                      />
                    </td>
                    <td>
                      <v-text-field
                        v-model="totals.amount"
                        readonly
                        :label="$lang('company-invoices.totals.total')"
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              v-model="form.causal"
              :label="$lang('company-invoices.fields.causal')"
              :error="!!validation.causal"
              :error-messages="!!validation.causal ? validation.causal : []"
            />
            <v-select
              v-model="form.payment_data.payment_term"
              label="Termini Pagamento"
              :items="paymentTerms"
            />
            <v-select
              v-model="form.payment_data.payment_method"
              label="Modalità Pagamento"
              :items="paymentMethods"
            />
            <v-text-field
              v-model="form.payment_data.beneficiary"
              label="Beneficiario"
            />
            <v-date
              v-model="form.payment_data.expires_at"
              label="Data Scadenza"
            />
            <v-text-field
              v-model="form.payment_data.amount"
              type="number"
              label="Importo"
            />
            <v-text-field
              v-model="form.payment_data.iban"
              label="IBAN"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
  import { mapState } from "vuex";

  import {
    CREATE_COMPANY_INVOICES_RESET,
    CREATE_COMPANY_INVOICES,
  } from "@/store/company-invoices/actions";

  import {
    GET_COMPANY_PAYMENTS_SINGLE
  } from "@/store/company-payments/actions";

  import form from "@/pages/company-invoices/mixins/form";

  export default {
    name: "CreationForm",
    mixins: [
      form
    ],
    props: {
      companyId: {
        type: Number,
        required: false
      },
      paymentId: {
        type: Number,
        required: false
      }
    },
    beforeMount() {
      if (this.companyId) {
        this.form.company_id = this.companyId;
      }
      
      if (this.paymentId) {
        this.form.company_payment_id = this.paymentId;

        this.$store.dispatch(GET_COMPANY_PAYMENTS_SINGLE, {id: this.paymentId}).then((response) => {
          this.form.details.push({
            company_payment_id: response.id,
            description: response.description,
            amount: response.amount,
            vat_type: null,
          });
        });
      }

      this.invoiceSeries = "CB1";
      this.type = this.$const('ConstDocumentTypes', 'INVOICE');
      this.form.date = this.$moment().format('YYYY-MM-DD');

      this.seriesChanged();
    },
    methods: {
      resetAction() {
        return CREATE_COMPANY_INVOICES_RESET;
      },
      submitAction() {
        return CREATE_COMPANY_INVOICES;
      }
    },
    computed: {
      ...mapState({
        loading: state => state.companyInvoices.creationRequest.loading,
        validation: state => state.companyInvoices.creationRequest.validation,
      }),
    },
    watch: {
      company: {
        handler() {
          this.form.details.map(item => {
            item.vat_type = this.defaultVat;
            item.vat_exclusion = this.defaultVat == 0 ? 'N2.1' : null;
          });
        }
      },
    }
  };
</script>

<style scoped>

</style>

